import { useEffect } from "react";
import type {
    LandingPageTemplateResponse,
    SegmentComponent,
} from "@/types/landing";
import styles from "./Wheel.module.css";
import type { Site } from "@/types/sites";
import { getRandomFloatInRange, logNewGoogleAnalyticsEvent } from "src/utils/utils";
import type { SpinStartEnd } from "@/components/Spinner";

type DrawProps = {
    spinningCanvasContext: CanvasRenderingContext2D;
    staticCanvasContext: CanvasRenderingContext2D;
    primaryColor: string;
    contrastColor: string;
    numSegments: number;
    setSpinStartEnd: React.Dispatch<React.SetStateAction<SpinStartEnd>>;
    segments?: SegmentComponent[];
};

const CANVAS_SIDE = 250; // canvas is a square, altering this variable will scale all aspects of the spinner up and down dynamically.
const DEFAULT_CANVAS_SIDE = 400; // This should never be altered, it is used to calculate the scale factor for the spinner.
const TWO_PI = Math.PI * 2; // 2π radians == 360°
const CANVAS_CENTRE = CANVAS_SIDE / 2;
const INNER_CIRCLE_RADIUS = CANVAS_SIDE / 8;

const DEFAULT_PRIMARY_COLOR = "#000000"; // default to black
const DEFAULT_CONTRAST_COLOR = "#FFFFFF"; // default to white

// the line gets thicker as the canvas gets bigger, this ensures the needle/triangle corners are
// embedded in the circle and don't look odd. Calcualted from known values
// e.g. at 400px canvas, line width should be 10px
const CIRCLE_LINE_WIDTH = CANVAS_SIDE * (10 / DEFAULT_CANVAS_SIDE);
const CIRCLE_RADIUS = CANVAS_CENTRE - CIRCLE_LINE_WIDTH; // allow for some padding to give a circular border

export const startSpin = (
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    spinStartEnd: SpinStartEnd
) => {
    logNewGoogleAnalyticsEvent("spinner-btn-clicked");

    const spinningCanvas =
        document.querySelector<HTMLCanvasElement>("#spinningCanvas");
    if (spinningCanvas) {
        const animations = spinningCanvas.getAnimations();
        if (animations.length === 1) {
            const animation = animations[0];
            if (animation.playState !== "running") {
                animation.play();
            }
        } else {
            // This code may run multiple times, we don't want to add a new listener each time so we
            // purposefully use an event handler property instead of an event listener.
            // Don't refactor this to use an event listener by mistake.
            //
            // https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Building_blocks/Events
            // With event handler properties, you can't add more than one handler for a single event.
            // For example, an event listener - `addEventListener('click', handler)` - can be added on
            // an element multiple times, with different functions specified in the second argument.
            // This is impossible with event handler properties because any subsequent attempts to set
            // the property will overwrite earlier ones.
            spinningCanvas.onanimationend = () => {
                logNewGoogleAnalyticsEvent("spinner-animation-finished");

                // don't trigger the modal until the animation has finished and "settled", otherwise it
                // looks to the user like it might still be spinning if you open the modal the same
                // milisecond that it stops spinning
                const animations = spinningCanvas.getAnimations();
                if (animations.length === 1) {
                    const animation = animations[0];
                    if (animation.playState === "finished") {
                        animation.commitStyles();
                        animation.cancel();
                        spinningCanvas?.classList?.remove(styles.spinning);
                    }
                }
                setTimeout(() => {
                    setIsModalOpen(true);
                    window.scrollTo(0, 0);
                    logNewGoogleAnalyticsEvent("spinner-modal-opened");

                    // currently this next line is only really for quick iteration / testing purposes, it's
                    // not needed in production as the modal can't be closed and the spinner can't be
                    // re-spun. If we do ever allow re-spins then this will become much more useful.
                    getSpinOffset(spinStartEnd);
                }, 250);
            };
            spinningCanvas?.classList?.add(styles.spinning);
        }
    }
};

type WheelType = ({
    landingPage,
    site,
    setIsModalOpen,
    winningSegmentIndex,
    spinStartEnd,
    setSpinStartEnd,
}: {
    landingPage: LandingPageTemplateResponse;
    site: Site;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    winningSegmentIndex: number;
    spinStartEnd: SpinStartEnd;
    setSpinStartEnd: React.Dispatch<React.SetStateAction<SpinStartEnd>>;
}) => React.ReactElement;

export const Wheel: WheelType = ({
    site,
    landingPage,
    setIsModalOpen,
    winningSegmentIndex,
    spinStartEnd,
    setSpinStartEnd,
}) => {
    useEffect(() => {
        getSpinOffset(spinStartEnd);
    }, [spinStartEnd]);

    // Prevent this part being parsed server side, it will fail as there is no `document` object.
    useEffect(() => {
        const root = document?.querySelector<HTMLElement>(":root");
        root?.style?.setProperty("--wheel-size", `${CANVAS_SIDE}px`);
    }, []);

    const primaryColor =
        site.attributes.onBackgroundColour || DEFAULT_PRIMARY_COLOR;
    const contrastColor =
        site.attributes.onBackgroundColour || DEFAULT_CONTRAST_COLOR;
    const segments =
        landingPage?.data?.attributes?.spinner?.data?.attributes?.segments;
    const numSegments: number = segments?.length || 0;

    useEffect(() => {
        const drawProps = getDrawProps(
            primaryColor,
            contrastColor,
            numSegments,
            setSpinStartEnd,
            segments
        );

        if (drawProps) {
            drawSegments(drawProps, winningSegmentIndex);
            drawInnerCircle(drawProps);
            drawTriangle(drawProps);
        }
    }, [
        contrastColor,
        numSegments,
        primaryColor,
        segments,
        setSpinStartEnd,
        winningSegmentIndex,
    ]);

    return (
        <>
            {numSegments > 0 ? (
                <div className={styles.canvasContainer}>
                    <div
                        id="wheel"
                        className={styles.wheel}
                        onClick={() => {
                            startSpin(setIsModalOpen, spinStartEnd);
                        }}
                    >
                        <canvas
                            id="spinningCanvas"
                            width={CANVAS_SIDE}
                            height={CANVAS_SIDE}
                        ></canvas>
                        <canvas
                            id="staticCanvas"
                            width={CANVAS_SIDE}
                            height={CANVAS_SIDE}
                        ></canvas>
                    </div>
                </div>
            ) : null}
        </>
    );
};

const setCanvasResolution = (
    canvas: HTMLCanvasElement,
    canvasContext: CanvasRenderingContext2D
) => {
    // Get the DPR and size of the canvas
    const dpr = window.devicePixelRatio;

    // Set the "actual" size of the canvas
    canvas.width = CANVAS_SIDE * dpr;
    canvas.height = CANVAS_SIDE * dpr;
    // Scale the context to ensure correct drawing operations
    canvasContext.scale(dpr, dpr);
    // Set the "drawn" size of the canvas
    canvas.style.width = `${CANVAS_SIDE}px`;
    canvas.style.height = `${CANVAS_SIDE}px`;
};

const getDrawProps = (
    primaryColor: string,
    contrastColor: string,
    numSegments: number,
    setSpinStartEnd: React.Dispatch<React.SetStateAction<SpinStartEnd>>,
    segments?: SegmentComponent[]
): DrawProps | null => {
    // Use a second canvas, so we can spin one, but leave the other static
    // Type casting is required, so we can use the properties of the canvas context
    const spinningCanvas =
        document.querySelector<HTMLCanvasElement>("#spinningCanvas");
    const staticCanvas =
        document.querySelector<HTMLCanvasElement>("#staticCanvas");
    if (!(spinningCanvas && staticCanvas)) {
        return null;
    }

    const styles = window.getComputedStyle(spinningCanvas);
    const fontSizeWithUnits = styles.getPropertyValue("font-size");
    const fontFamily = styles.getPropertyValue("font-family");
    const fontWeight = styles.getPropertyValue("font-weight");

    // scale font size based on canvas size, 32px @ 400px canvas
    let fontSize = Number(
        fontSizeWithUnits.substring(0, fontSizeWithUnits.length - 2)
    );
    const units = fontSizeWithUnits.substring(
        fontSizeWithUnits.length - 2,
        fontSizeWithUnits.length
    );
    fontSize = (CANVAS_SIDE / DEFAULT_CANVAS_SIDE) * fontSize;

    const spinningCanvasContext = spinningCanvas.getContext("2d");
    const staticCanvasContext = staticCanvas.getContext("2d");
    if (!(spinningCanvasContext && staticCanvasContext)) {
        return null;
    }

    setCanvasResolution(staticCanvas, staticCanvasContext);
    setCanvasResolution(spinningCanvas, spinningCanvasContext);

    spinningCanvasContext.font = `${fontWeight} ${fontSize}${units} ${fontFamily}`;

    return {
        spinningCanvasContext: spinningCanvasContext,
        staticCanvasContext: staticCanvasContext,
        primaryColor,
        contrastColor,
        numSegments,
        setSpinStartEnd,
        segments,
    };
};

const getSpinOffset = (spinStartEnd: SpinStartEnd): void => {
    const completeSpins = 3; // number of complete spins to execute before landing on segment

    const spinOffset = getRandomFloatInRange(
        spinStartEnd.start,
        spinStartEnd.end
    );

    // The wheel spins clockwise, but the angle we have would bring the segment to the 12 o'clock position
    // if we were spinning anti-clockwise. So we need to flip the angle, by subtracting it from 2π (a full circle)
    const radiansInDirectionOfSpin = TWO_PI - spinOffset;
    const extraSpins = completeSpins * TWO_PI;
    const totalRadians = radiansInDirectionOfSpin + extraSpins;

    const root = document?.querySelector<HTMLElement>(":root");
    root?.style?.setProperty("--wheel-spin-radians", `${totalRadians}rad`);
};

const drawSegments = (props: DrawProps, winningSegmentIndex: number) => {
    const {
        spinningCanvasContext,
        primaryColor,
        numSegments,
        setSpinStartEnd,
    } = props;
    if (spinningCanvasContext) {
        // Draw outer circle
        drawCircle(spinningCanvasContext, CIRCLE_RADIUS);

        // Give it a border
        spinningCanvasContext.lineWidth = CIRCLE_LINE_WIDTH;
        spinningCanvasContext.strokeStyle = primaryColor;
        spinningCanvasContext.stroke();

        // Draw segments
        const segmentWidthInRadians = TWO_PI / numSegments; // Each segment is 1/n of the circle. Based on the 2π / n formula.
        // Canvas default is to start at 3 o'clock, we want to start at ~12 o'clock.
        // I write ~ (approximately), because we want to start in the middle of the segment, not at the edge of it.
        const startingOffset = -1 * (Math.PI / 2) - segmentWidthInRadians / 2;
        let lastAngle = startingOffset;
        for (let i = 0; i < numSegments; i++) {
            const angle = segmentWidthInRadians * (i + 1) + startingOffset;
            spinningCanvasContext.save();
            drawSegment(props, i, lastAngle, angle);
            if (i === winningSegmentIndex) {
                // Ensure the spinner stops spinning with a decent margin on each side of the
                // segment so that there is no chance someone thinks it landed on the segment next
                // to it
                // If you make the margin too large then it takes away from the excitement / risk
                // If you make it too small then you might as well not have it.
                // Suggest using 1/6th, 1/8th and 1/10th as the values you play around with.
                const oneTenthOfASegmentWidthRadians =
                    segmentWidthInRadians / 10;

                // "lastAngle" is the angle of the segment start from the 3 o'clock position
                // but our spin animation cares about the angle from the 12 o'clock position
                // (because that is where the pointer is pointing), so add on π/2 (1/4 of a circle)
                // to get the angle from the 12 o'clock position
                const radiansToSegmentStart = lastAngle + Math.PI / 2;

                // radiants === radians - @mpuxted plays sooo many computer games, he makes typos in
                // his code... https://valorant.fandom.com/wiki/Radiants
                const radiantsToSegmentEnd =
                    radiansToSegmentStart + segmentWidthInRadians;
                setSpinStartEnd({
                    start:
                        radiansToSegmentStart + oneTenthOfASegmentWidthRadians,
                    end: radiantsToSegmentEnd - oneTenthOfASegmentWidthRadians,
                });
            }
            spinningCanvasContext.restore();
            lastAngle = angle;
        }
    }
};

const drawSegment = (
    props: DrawProps,
    segmentIndex: number,
    lastAngle: number,
    angle: number
) => {
    const { spinningCanvasContext, contrastColor, segments } = props;
    if (segments) {
        const segment = segments[segmentIndex];
        if (spinningCanvasContext) {
            spinningCanvasContext.beginPath();
            spinningCanvasContext.moveTo(CANVAS_CENTRE, CANVAS_CENTRE);
            spinningCanvasContext.arc(
                CANVAS_CENTRE,
                CANVAS_CENTRE,
                CIRCLE_RADIUS,
                lastAngle,
                angle,
                false
            );
            spinningCanvasContext.lineTo(CANVAS_CENTRE, CANVAS_CENTRE);
            spinningCanvasContext.closePath();
            spinningCanvasContext.fillStyle = segment?.colour;
            spinningCanvasContext.fill();

            const spinnerText = segment?.spinnerText;
            if (spinnerText) {
                //  Moves the center of the canvas to the center of the circle
                // This is necessary because the canvas context normally rotates around the top left corner of the canvas.
                // Because we want to rotate the text around the center of the spinner, we need to move the center of the canvas to the center of the spinner.
                spinningCanvasContext.translate(CANVAS_CENTRE, CANVAS_CENTRE);
                // Rotate the canvas so we start writing in the horizontal centre of the segment
                spinningCanvasContext.rotate((lastAngle + angle) / 2);
                // Move the canvas to the edge of the circle
                spinningCanvasContext.translate(CIRCLE_RADIUS, 0);
                // Rotate again so that our text direction matches the direction of the segment
                spinningCanvasContext.rotate(degreesToRadians(90));

                spinningCanvasContext.textBaseline = "middle";
                spinningCanvasContext.textAlign = "center";
                spinningCanvasContext.fillStyle = contrastColor;

                // position text 40% of the way from the edge of the circle
                const textDistanceFromEdge = CIRCLE_RADIUS * 0.4;
                spinningCanvasContext.fillText(
                    spinnerText,
                    0,
                    textDistanceFromEdge
                );
            }
        }
    }
};

const degreesToRadians = (degrees: number): number => {
    // degrees * (π / 180) = radians
    return degrees * (Math.PI / 180);
};
const drawCircle = (
    canvasContext: CanvasRenderingContext2D,
    radius: number
) => {
    canvasContext.beginPath();
    canvasContext.arc(CANVAS_CENTRE, CANVAS_CENTRE, radius, 0, TWO_PI, false);
    canvasContext.closePath();
};

const drawInnerCircle = (props: DrawProps) => {
    const { staticCanvasContext, primaryColor, contrastColor } = props;
    if (staticCanvasContext) {
        drawCircle(staticCanvasContext, INNER_CIRCLE_RADIUS);

        staticCanvasContext.fillStyle = primaryColor;
        staticCanvasContext.fill();

        // hide the edges of the triangle by making the line of the circle thicker
        staticCanvasContext.strokeStyle = contrastColor;
        staticCanvasContext.lineWidth = CIRCLE_LINE_WIDTH;
        staticCanvasContext.stroke();
    }
};

const drawTriangle = (props: DrawProps) => {
    const { staticCanvasContext, contrastColor } = props;

    // Using these factors makes the spike look good no matter how big or small the canvas is
    // Initially calculated using known values e.g. 400px canvas size, desired width is 30px and height is 20px.
    const spikeWidthResizingFactor = 30 / DEFAULT_CANVAS_SIDE;
    const spikeHeightResizingFactor = 20 / DEFAULT_CANVAS_SIDE;

    // the spike sits away from the centre as it is an odd shape ( a bit like this: ô ) we want the
    // centre of the 2 circles overlayed but the triangle part styled above.
    const spikeCentreOffset = INNER_CIRCLE_RADIUS;

    const spikeWidth =
        INNER_CIRCLE_RADIUS - CANVAS_SIDE * spikeWidthResizingFactor;
    const spikeHeight =
        INNER_CIRCLE_RADIUS + CANVAS_SIDE * spikeHeightResizingFactor;

    if (staticCanvasContext) {
        staticCanvasContext.beginPath();
        staticCanvasContext.moveTo(
            CANVAS_CENTRE + spikeWidth,
            CANVAS_CENTRE - spikeCentreOffset
        );
        staticCanvasContext.lineTo(
            CANVAS_CENTRE - spikeWidth,
            CANVAS_CENTRE - spikeCentreOffset
        );
        staticCanvasContext.lineTo(CANVAS_CENTRE, CANVAS_CENTRE - spikeHeight);
        staticCanvasContext.closePath();

        staticCanvasContext.fillStyle = contrastColor;
        staticCanvasContext.fill();
    }
};
